import { Routes, Route, Navigate } from 'react-router-dom';
import Layout from './components/layout/Layout.jsx';
import ProtectedRoute from './components/ProtectedRoute.jsx';
import RoleGuard from './components/layout/RoleGuard.jsx';
import { useAuth } from './hooks/useAuth.js';
import Dashboard from './pages/Dashboard.jsx';
import Leads from './pages/Leads.jsx';
import LeadDetail from './pages/LeadDetail.jsx';
import Conversations from './pages/Conversations.jsx';
import Showings from './pages/Showings.jsx';
import Followups from './pages/Followups.jsx';
import Team from './pages/Team.jsx';
import Analytics from './pages/Analytics.jsx';
import Reports from './pages/Reports.jsx';
import Settings from './pages/Settings.jsx';
import Enrich from './pages/Enrich.jsx';
import Login from './pages/Login.jsx';
import Profile from './pages/Profile.jsx';
import AuthCallback from './pages/AuthCallback.jsx';
import AdminClients from './pages/admin/Clients.jsx';
import AdminClientDetail from './pages/admin/ClientDetail.jsx';
import AdminUsage from './pages/admin/Usage.jsx';

// Root route resolver. Super admins land on the client dashboard; everyone
// else continues to see the morning briefing.
function RoleHome() {
  const { user } = useAuth();
  if (user?.role === 'super_admin') {
    return <Navigate to="/admin/clients" replace />;
  }
  return <Navigate to="/dashboard" replace />;
}

// Every feature is defined once and mounted at BOTH /path and /re/path.
// Internal demo docs, prospect-facing URLs, and support links all reference
// /re/* — keeping the aliases means neither form breaks.
const PROTECTED_ROUTES = [
  { path: 'dashboard', element: <Dashboard /> },
  { path: 'profile', element: <Profile /> },

  {
    path: 'leads',
    element: (
      <RoleGuard allow={['team_leader', 'agent', 'isa']}>
        <Leads />
      </RoleGuard>
    ),
  },
  {
    path: 'leads/:id',
    element: (
      <RoleGuard allow={['team_leader', 'agent', 'isa']}>
        <LeadDetail />
      </RoleGuard>
    ),
  },

  {
    path: 'conversations',
    element: (
      <RoleGuard allow={['team_leader']}>
        <Conversations />
      </RoleGuard>
    ),
  },
  {
    path: 'showings',
    element: (
      <RoleGuard allow={['team_leader']}>
        <Showings />
      </RoleGuard>
    ),
  },

  // Followups ships at /followups historically; the /re/* prompts use the
  // hyphenated /follow-ups form, so both spellings are aliased below.
  {
    path: 'followups',
    element: (
      <RoleGuard allow={['team_leader', 'isa']}>
        <Followups />
      </RoleGuard>
    ),
  },
  {
    path: 'follow-ups',
    element: (
      <RoleGuard allow={['team_leader', 'isa']}>
        <Followups />
      </RoleGuard>
    ),
  },

  {
    path: 'team',
    element: (
      <RoleGuard allow={['team_leader']}>
        <Team />
      </RoleGuard>
    ),
  },
  {
    path: 'analytics',
    element: (
      <RoleGuard allow={['team_leader']}>
        <Analytics />
      </RoleGuard>
    ),
  },
  {
    path: 'reports',
    element: (
      <RoleGuard allow={['team_leader']}>
        <Reports />
      </RoleGuard>
    ),
  },
  {
    path: 'settings',
    element: (
      <RoleGuard allow={['team_leader']}>
        <Settings />
      </RoleGuard>
    ),
  },
  {
    path: 'enrich',
    element: (
      <RoleGuard allow={['team_leader']}>
        <Enrich />
      </RoleGuard>
    ),
  },

  {
    path: 'admin/clients',
    element: (
      <RoleGuard allow={['super_admin']}>
        <AdminClients />
      </RoleGuard>
    ),
  },
  {
    path: 'admin/clients/:id',
    element: (
      <RoleGuard allow={['super_admin']}>
        <AdminClientDetail />
      </RoleGuard>
    ),
  },
  {
    path: 'admin/usage',
    element: (
      <RoleGuard allow={['super_admin']}>
        <AdminUsage />
      </RoleGuard>
    ),
  },
];

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route path="/" element={<RoleHome />} />
        <Route path="/re" element={<RoleHome />} />

        {PROTECTED_ROUTES.flatMap((r) => [
          <Route key={r.path} path={`/${r.path}`} element={r.element} />,
          <Route key={`re-${r.path}`} path={`/re/${r.path}`} element={r.element} />,
        ])}

        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Route>
    </Routes>
  );
}
